import store from '..';
import { Host } from '../../models';
import { Hosts, SioBridge } from '../../services';
import { createRestSlices, applyReducers } from './rest';

const {
    initialState,
    getReducer, listReducer, updateReducer,
    getAction, listAction, updateAction
} = createRestSlices(Hosts);

initialState.hosts = [];
initialState.lastAction = null;
initialState.lastActionResult = null;
initialState.lastActionStdOut = null;
initialState.lastActionStdErr = null;
initialState.lastActionError = null;
initialState.isRunningAction = false;
initialState.isForgetting = false;
initialState.successfullyForgot = false;
initialState.failedToForget = false;

const RESET = 'kronos/hosts/RESET';
const RESET_ACTIONS = 'kronos/hosts/RESET_ACTIONS';
const RUN_ACTION = 'kronos/hosts/RUN_ACTION';
const RUN_ACTION_SUCCEEDED = 'kronos/hosts/RUN_ACTION_SUCCEEDED';
const RUN_ACTION_FAILED = 'kronos/hosts/RUN_ACTION_FAILED';
const LOADING_HOSTS_SUCCESS = 'kronos/hosts/LOADING_ITEMS_SUCCESS';
const FORGET = 'kronos/hosts/FORGET';
const FORGET_SUCCEEDED = 'kronos/hosts/FORGET_SUCCEEDED';
const FORGET_FAILED = 'kronos/hosts/FORGET_FAILED';

const actionsReducer = (state = initialState, action) => {
    switch(action.type) {
        case RESET_ACTIONS:
            return {
                ...state,
                lastAction: null,
                lastActionResult: null,
                lastActionStdOut: null,
                lastActionStdErr: null,
                lastActionError: null,
                isForgetting: false,
                successfullyForgot: false,
                failedToForget: false,
            };
        case RUN_ACTION:
            return {
                ...state,
                lastAction: action.name,
                lastActionResult: null,
                lastActionError: null,
                isRunningAction: true,
            };
        case RUN_ACTION_SUCCEEDED:
            return {
                ...state,
                lastActionResult: true,
                lastActionStdOut: action.result?.stdout,
                lastActionStdErr: action.result?.stderr,
                lastActionError: action.error,
                isRunningAction: false,
            };
        case RUN_ACTION_FAILED:
            return {
                ...state,
                lastActionResult: false,
                lastActionStdOut: action.result?.stdout,
                lastActionStdErr: action.result?.stderr,
                lastActionError: action.error,
                isRunningAction: false,
            };
        default:
            return state;
    }
};

const forgetReducer = (state = initialState, action) => {
    switch(action.type) {
        case RESET:
            return {
                ...state,
                hosts: [],
                host: null,
                isForgetting: false,
                successfullyForgot: false,
                failedToForget: false,
            };
        case LOADING_HOSTS_SUCCESS:
            return {
                ...state,
                isForgetting: false,
                successfullyForgot: false,
                failedToForget: false,
            };
        case FORGET:
            return {
                ...state,
                isForgetting: true,
                successfullyForgot: false,
                failedToForget: false,
            };
        case FORGET_SUCCEEDED:
            return {
                ...state,
                isForgetting: false,
                successfullyForgot: true,
                failedToForget: false,
            };
        case FORGET_FAILED:
            return {
                ...state,
                isLoadingOne: false,
                isForgetting: false,
                successfullyForgot: false,
                failedToForget: true,
            };
        default:
            return state;
    }
};
/* Export reducer */
export default (state = initialState, action) => {
    return applyReducers(state, action, [
        getReducer, listReducer, updateReducer,
        actionsReducer, forgetReducer
    ]);
}

/* Export CRUD actions */
export const loadHost   = getAction;
export const loadHosts  = listAction;
export const updateHost = updateAction;

export const resetHosts = () => dispatch => dispatch({ type: RESET });
export const resetHostActions = () => dispatch => dispatch({ type: RESET_ACTIONS });

const HOST_ACTION_TIMEOUT_MS = 30 * 1000;

const withTimeout = (promise, timeoutMs) => {
    let timeoutId;
    const timeout = new Promise((_resolve, reject) => {
        timeoutId = setTimeout(
            () => reject(new Error('Host action timed out')),
            timeoutMs
        );
    });

    return Promise.race([promise, timeout]).finally(() => {
        clearTimeout(timeoutId);
    });
};

export const runHostAction = (hostId, action, params, callback) => dispatch => {
    dispatch({ type: RUN_ACTION, name: action });
    withTimeout(Hosts.runAction(hostId, action, params), HOST_ACTION_TIMEOUT_MS)
        .then(result => {
            dispatch({
                type: RUN_ACTION_SUCCEEDED,
                result
            });
            callback && callback(/*err*/null, result);
        }).catch(error => {
            dispatch({
                type: RUN_ACTION_FAILED,
                error
            });
            callback && callback(error);
        });
}

export const forgetHost = (host, callback) => dispatch => {
    dispatch({ type: FORGET });
    Hosts.forget(host)
        .then(result => {
            dispatch({
                type: FORGET_SUCCEEDED,
                result
            });
            callback && callback(/*err*/null, result);
        }).catch(error => {
            dispatch({
                type: FORGET_FAILED,
                error
            });
            callback && callback(error);
        });
}

SioBridge.on('hosts_update', (data) => {
    let connectedHosts = data.map(host => new Host(host));

    const storeHosts = (store.getState().hosts.hosts || []).map(host => {
        host.status = 'disconnected';
        connectedHosts.forEach(connectedHost => {
            if (connectedHost._id === host._id) {
                host.status = 'connected';
                connectedHosts = connectedHosts.filter(connectedHost => connectedHost._id !== host._id);
            }
        });

        return host;
    });
    const hosts = storeHosts.concat(connectedHosts);

    // let's check if we have just run the "Restart pihyve" or "Reboot" action:
    // in this case, since pihyve stops on the host, we received a hosts_update
    // msg without the current host which will make it disappear and show a
    // black screen on the management page.
    // The solution is to push this disconnected host.
    const { host, lastAction } = store.getState().hosts;
    if (host && [Host.ACTION_REBOOT, Host.ACTION_RESTART_PIHYVE].includes(lastAction) && !hosts.find(h => h._id === host._id))
        hosts.push(new Host({
            ...host,
            status: 'disconnected'
        }));

    store.dispatch({
        type: 'kronos/hosts/LOADING_ITEMS_SUCCESS',
        hosts
    });
});
